import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClockIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { currencyService } from '../../services/currencyService';
import { Card, Loading } from '../common';

const RateChangeLog = () => {
  const { t } = useTranslation();
  const [rateHistory, setRateHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRateHistory();
  }, []);

  const fetchRateHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await currencyService.getAllRateHistory({ limit: 5 });
      if (response.success) {
        setRateHistory(response.data.history || []);
      } else {
        setError(t('common.error'));
      }
    } catch (err) {
      console.error('Failed to fetch rate history:', err);
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const formatRate = (rate) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    }).format(rate);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRateChangeIndicator = (oldRate, newRate) => {
    if (newRate > oldRate) {
      return <span className="text-green-600 dark:text-green-400 text-xs font-medium ml-1">+</span>;
    } else if (newRate < oldRate) {
      return <span className="text-red-600 dark:text-red-400 text-xs font-medium ml-1">-</span>;
    }
    return null;
  };

  if (loading) {
    return (
      <Card title={t('dashboard.rateChangeLog') || 'Rate Change Log'}>
        <div className="flex items-center justify-center h-40">
          <Loading size="md" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card title={t('dashboard.rateChangeLog') || 'Rate Change Log'}>
        <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400">
          {error}
        </div>
      </Card>
    );
  }

  return (
    <Card title={t('dashboard.rateChangeLog') || 'Rate Change Log'}>
      {rateHistory.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-gray-500 dark:text-gray-400">
          <ClockIcon className="h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">{t('common.noData') || 'No rate changes yet'}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rateHistory.map((item, index) => (
            <div
              key={item.id || index}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-100 dark:border-gray-700"
            >
              <div className="flex-1 min-w-0">
                {/* Currency Pair */}
                <div className="flex items-center text-sm font-semibold text-gray-900 dark:text-gray-100">
                  <span>{item.fromCurrency?.code || 'N/A'}</span>
                  <ArrowRightIcon className="h-3 w-3 mx-1 text-gray-400 dark:text-gray-500" />
                  <span>{item.toCurrency?.code || 'N/A'}</span>
                  {getRateChangeIndicator(item.oldRate, item.newRate)}
                </div>

                {/* Rate Change */}
                <div className="flex items-center mt-1 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">
                    {formatRate(item.oldRate)}
                  </span>
                  <ArrowRightIcon className="h-2.5 w-2.5 mx-1 text-gray-400 dark:text-gray-500" />
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    {formatRate(item.newRate)}
                  </span>
                </div>

                {/* Changed by and time */}
                <div className="flex items-center mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <span className="truncate max-w-[120px]" title={item.changedBy?.fullName || item.changedBy?.username || 'System'}>
                    {item.changedBy?.fullName || item.changedBy?.username || 'System'}
                  </span>
                  <span className="mx-1.5">|</span>
                  <ClockIcon className="h-3 w-3 mr-1" />
                  <span>{formatDate(item.createdAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default RateChangeLog;
